/**
 * Search bounds for site plan visibility.
 *
 * The backend matches communities by their marker point, and a community's site plan
 * is only fetched while the community is in the results. Once site plans render
 * (zoom >= SITE_PLAN_RENDER_ZOOM_THRESHOLD) a polygon can still be on screen while its
 * marker point has left the viewport, so searching the bare viewport would drop the
 * community and its site plan with it.
 *
 * At site plan zoom the search bounds are therefore:
 * - the viewport expanded by a fixed ~1 mile tolerance, which covers communities whose
 *   site plan is not loaded yet; and
 * - the bounding box of every loaded site plan that intersects the viewport, which
 *   contains that community's marker point regardless of the plan's size or where the
 *   marker sits inside it.
 *
 * Used by every viewport-driven search (focused, rectangular area and State refresh).
 *
 * Pure functions — no side effects, no SDK coupling.
 *
 * @module searchResultsMap/services/sitePlanViewportPadding
 */

import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from "../constants";
import { areBoundsValid, isPolygonInBounds } from "./geometry";

/**
 * Constant viewport expansion in decimal degrees.
 *
 * ~0.015° latitude ≈ 1 mile / 1.6 km.
 * Longitude distance varies slightly by latitude.
 *
 * The padding is intentionally small:
 * enough to preserve partially-visible site plans near viewport edges,
 * while avoiding excessive overfetching outside the visible map area.
 */
const SITE_PLAN_VIEWPORT_PADDING = 0.015;

/**
 * Returns the viewport padding amount in decimal degrees.
 * Padding is only applied when site plans are active.
 *
 * @param {number} zoom
 * @returns {number} Padding in decimal degrees
 */
export function getViewportPaddingForSitePlans(zoom) {
  if (zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD) {
    return 0;
  }

  return SITE_PLAN_VIEWPORT_PADDING;
}

/**
 * Expands viewport bounds by the provided padding amount.
 * Returns a new bounds object; does not mutate the input.
 *
 * @param {{ south: number, north: number, west: number, east: number }} bounds
 * @param {number} padding - Expansion amount in decimal degrees
 * @returns {{ south: number, north: number, west: number, east: number }}
 */
export function expandBounds(bounds, padding) {
  if (!padding || !bounds) {
    return bounds;
  }

  return {
    south: bounds.south - padding,
    north: bounds.north + padding,
    west: bounds.west - padding,
    east: bounds.east + padding,
  };
}

function getSitePlanBounds(sitePlan) {
  const bounds = {
    south: sitePlan?.bottomLeftLatitude,
    north: sitePlan?.topRightLatitude,
    west: sitePlan?.bottomLeftLongitude,
    east: sitePlan?.topRightLongitude,
  };

  return areBoundsValid(bounds) ? bounds : null;
}

function unionBounds(base, extension) {
  return {
    south: Math.min(base.south, extension.south),
    north: Math.max(base.north, extension.north),
    west: Math.min(base.west, extension.west),
    east: Math.max(base.east, extension.east),
  };
}

/**
 * Returns the bounds a viewport-driven search should request.
 * Below site plan zoom this is the viewport itself.
 *
 * @param {{ south: number, north: number, west: number, east: number }} viewport
 * @param {number} zoom
 * @param {Array<Object>|null} sitePlans - Loaded site plans from the store.
 * @returns {{ south: number, north: number, west: number, east: number }}
 */
export function getSearchBounds(viewport, zoom, sitePlans) {
  const padding = getViewportPaddingForSitePlans(zoom ?? 0);
  if (!padding || !areBoundsValid(viewport)) {
    return viewport;
  }

  return (Array.isArray(sitePlans) ? sitePlans : [])
    .filter((sitePlan) => isPolygonInBounds(sitePlan, viewport))
    .map(getSitePlanBounds)
    .filter(Boolean)
    .reduce(unionBounds, expandBounds(viewport, padding));
}
